import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import {
  useGetChamberQuery,
  useUpdateChamberMutation,
} from "../../../../graphql/types";
import { useGetUserFromUserIdInAuthState } from "../../../../hooks/useGetUserFromUserIdInAuth";
import { ActionSuccessDialog } from "../../../common/dashboard/ActionSuccessDialog";
import { ConfirmDialog } from "../../../common/dashboard/ConfirmDialog";
import Loader from "../../../common/Loader";
import UnsavedChangesPrompt from "../../../common/UnsavedChangesPrompt";
import ManageChamberProfileSections from "./ManageChamberProfileSections";
import {
  ChamberProfileFormValues,
  createChamberProfileInitialValues,
  createChamberProfileValidationSchema,
  createChamberProfileUpdateInput,
} from "./manageChamberProfileModel";
import {
  hasUnsavedChamberProfileWork,
  shouldHydrateChamberProfile,
} from "./manageChamberProfileDirtyState";
import { useCmpOrganizationNoun } from "../../../../contexts/CmpOrganizationNounContext";
import { CALLER_HANDLED_USER_ERROR_CONTEXT } from "../../../../utils/apolloErrorPolicy";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
  STAFF_SUPPORT_EMAIL,
} from "../../../../utils/userFacingError";

const MAX_LOGO_FILE_SIZE_BYTES = 5 * 1024 * 1024;

const convertImageToBase64 = ({ file }: { file: File }): Promise<string> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => resolve(String(fileReader.result || ""));
    fileReader.onerror = reject;
  });

type UploadFileResult =
  | { file: File; error?: undefined }
  | { file?: undefined; error?: string };

const getUploadFile = ({
  event,
}: {
  event: React.ChangeEvent<HTMLInputElement>;
}): UploadFileResult => {
  const file = event.target.files?.[0];
  event.target.value = "";

  if (!file) {
    return {};
  }

  if (!file.type.startsWith("image/")) {
    return { error: "This file is not an image. Choose an image file, for example a PNG or JPG." };
  }

  if (file.size > MAX_LOGO_FILE_SIZE_BYTES) {
    return { error: "This image is larger than 5 MB. Choose a smaller image file." };
  }

  return { file };
};

const ManageChamberProfile = (): React.ReactElement => {
  const noun = useCmpOrganizationNoun();
  const validationSchema = useMemo(
    () => createChamberProfileValidationSchema(noun),
    [noun],
  );
  const [saving, setSaving] = useState(false);
  const [activeImageReadCount, setActiveImageReadCount] = useState(0);
  const [saveError, setSaveError] = useState("");
  const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState<string | null>(
    null,
  );
  const {
    user: authenticatedUser,
    loading: userLoading,
    error: userError,
  } = useGetUserFromUserIdInAuthState();
  const chamberId = authenticatedUser?.chamberId;
  const {
    data: chamberData,
    loading: chamberLoading,
    error: chamberError,
  } = useGetChamberQuery({
    variables: { chamberId: chamberId || "" },
    skip: !chamberId,
    // Load and save errors show on this page, not the full-page error.
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const chamber = chamberData?.getChamber;
  const [updateChamber] = useUpdateChamberMutation({
    context: CALLER_HANDLED_USER_ERROR_CONTEXT,
  });
  const initialValues = useMemo(
    () => createChamberProfileInitialValues({ chamber }),
    [chamber],
  );
  const lastSeenChamberProfileRef = useRef<{
    chamberId: string;
    values: ChamberProfileFormValues;
  } | null>(null);
  const pendingChamberProfileRef = useRef<{
    chamberId: string;
    values: ChamberProfileFormValues;
  } | null>(null);
  const hydratedChamberIdRef = useRef("");

  const formik = useFormik<ChamberProfileFormValues>({
    initialValues,
    enableReinitialize: false,
    validationSchema,
    onSubmit: async (values) => {
      setSaving(true);
      setSaveError("");

      try {
        const input = createChamberProfileUpdateInput({ values });
        const { data: updateData } = await updateChamber({
          variables: { id: input.id, input },
          refetchQueries: ["getUser", "getChamber"],
          awaitRefetchQueries: true,
        });

        const updatedChamber = updateData?.updateChamber;
        if (!updatedChamber) {
          // The save may have reached the server, so staff check before a second try.
          const unconfirmedMessage = "We could not confirm that your changes were saved. Refresh the page to check them. If your changes are missing, enter them again and select UPDATE.";
          setSaveError(unconfirmedMessage);
          setErrorDialogMessage(unconfirmedMessage);
          return false;
        }
        if (updatedChamber) {
          pendingChamberProfileRef.current = null;
          formik.resetForm({
            values: createChamberProfileInitialValues({
              chamber: updatedChamber,
            }),
          });
        }
        setUpdateSuccessOpen(true);
        return true;
      } catch (error) {
        const message = getUserFacingErrorText(
          error,
          buildStaffErrorFallback(`save the ${noun.singular} profile`)
        );
        setSaveError(message);
        setErrorDialogMessage(
          `Your ${noun.singular} profile was not saved. ${message}`
        );
        return false;
      } finally {
        setSaving(false);
      }
    },
  });
  const {
    initialValues: savedProfileValues,
    resetForm: resetProfileForm,
    values: currentProfileValues,
  } = formik;

  useEffect(() => {
    if (!chamberId || !chamber) {
      return;
    }

    const incomingValues = createChamberProfileInitialValues({ chamber });
    const lastSeenProfile = lastSeenChamberProfileRef.current;
    const incomingProfileChanged =
      !lastSeenProfile ||
      lastSeenProfile.chamberId !== chamberId ||
      hasUnsavedChamberProfileWork({
        activeImageReadCount: 0,
        baseline: lastSeenProfile.values,
        current: incomingValues,
      });

    if (incomingProfileChanged) {
      const nextProfile = { chamberId, values: incomingValues };
      lastSeenChamberProfileRef.current = nextProfile;
      pendingChamberProfileRef.current = nextProfile;
    }

    const pendingProfile = pendingChamberProfileRef.current;
    if (
      !pendingProfile ||
      pendingProfile.chamberId !== chamberId ||
      !shouldHydrateChamberProfile({
        activeChamberId: hydratedChamberIdRef.current,
        activeImageReadCount,
        baseline: savedProfileValues,
        current: currentProfileValues,
        incomingChamberId: pendingProfile.chamberId,
      })
    ) {
      return;
    }

    pendingChamberProfileRef.current = null;
    hydratedChamberIdRef.current = pendingProfile.chamberId;
    resetProfileForm({ values: pendingProfile.values });
  }, [
    activeImageReadCount,
    chamber,
    chamberId,
    currentProfileValues,
    resetProfileForm,
    savedProfileValues,
  ]);

  useEffect(() => {
    if (formik.submitCount < 1 || Object.keys(formik.errors).length === 0) {
      return;
    }

    const firstInvalidField = document.querySelector<HTMLElement>(
      '[aria-invalid="true"], .border-red-600',
    );
    firstInvalidField?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [formik.errors, formik.submitCount]);

  useEffect(() => {
    if (!updateSuccessOpen) return undefined;

    const timer = window.setTimeout(() => setUpdateSuccessOpen(false), 3000);
    return () => window.clearTimeout(timer);
  }, [updateSuccessOpen]);

  const handleLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { file, error } = getUploadFile({ event });
      if (error) setErrorDialogMessage(error);
      if (!file) return;

      setActiveImageReadCount((current) => current + 1);
      try {
        const base64 = await convertImageToBase64({ file });
        await formik.setFieldValue("logo", base64);
      } finally {
        setActiveImageReadCount((current) => Math.max(0, current - 1));
      }
    },
    [formik],
  );

  const handleSponsorLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { file, error } = getUploadFile({ event });
      if (error) setErrorDialogMessage(error);
      if (!file) return;

      setActiveImageReadCount((current) => current + 1);
      try {
        const base64 = await convertImageToBase64({ file });
        await formik.setFieldValue("sponsorLogo", base64);
      } finally {
        setActiveImageReadCount((current) => Math.max(0, current - 1));
      }
    },
    [formik],
  );

  const handleLogoDelete = useCallback(() => {
    void formik.setFieldValue("logo", undefined);
  }, [formik]);

  const handleSponsorLogoDelete = useCallback(() => {
    void formik.setFieldValue("sponsorLogo", undefined);
    void formik.setFieldValue("sponsorLogoLink", "");
  }, [formik]);

  if (userLoading || (chamberId && chamberLoading)) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (userError || chamberError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-700">
        {`We could not load your ${noun.singular} profile. Refresh the page and try again. If it keeps happening, email ${STAFF_SUPPORT_EMAIL}.`}
      </div>
    );
  }

  if (!chamberId || !chamber) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
        {`Your sign-in is not linked to a ${noun.singular} profile. Email ${STAFF_SUPPORT_EMAIL} for help.`}
      </div>
    );
  }

  return (
    <>
      <UnsavedChangesPrompt
        when={hasUnsavedChamberProfileWork({
          activeImageReadCount,
          baseline: formik.initialValues,
          current: formik.values,
        })}
        saving={saving || activeImageReadCount > 0}
        // Design (16): "Save & Update" runs the page's UPDATE (same checks).
        onSave={async () => {
          const errors = await formik.validateForm();
          if (Object.keys(errors).length) {
            await formik.submitForm();
            return false;
          }
          return (await formik.submitForm()) === true;
        }}
        savedMessage="Profile Updated Successfully!"
      />
      <form
        onSubmit={formik.handleSubmit}
        autoComplete="off"
        className="rounded-2xl bg-white px-3 py-[30px] sm:px-5"
      >
        <ManageChamberProfileSections
          formik={formik}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
          onSponsorLogoUpload={handleSponsorLogoUpload}
          onSponsorLogoDelete={handleSponsorLogoDelete}
        />

        {saveError ? (
          <p className="mx-auto my-4 max-w-[1096px] text-center font-medium text-red-600">
            {saveError}
          </p>
        ) : null}

        {Object.keys(formik.errors).length > 0 && formik.submitCount > 0 ? (
          <p className="mx-auto my-4 max-w-[1096px] text-center font-medium text-red-600">
            Some fields need changes. Fix the fields marked in red, then select UPDATE.
          </p>
        ) : null}

        <div className="mb-4 mt-8 flex justify-center px-3">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex w-full max-w-[320px] items-center justify-center gap-2 rounded-xl bg-cpaGold px-8 py-[14px] text-lg font-bold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:bg-gray-300 sm:w-auto sm:max-w-none sm:px-24 sm:py-[15px] sm:text-2xl"
          >
            {saving ? <Loader isWhite /> : "UPDATE"}
          </button>
        </div>
      </form>
      <ConfirmDialog
        open={updateSuccessOpen}
        variant="success"
        appearance="adminChamberSuccess"
        title="Profile Updated Successfully!"
        onClose={() => setUpdateSuccessOpen(false)}
      />
      <ActionSuccessDialog
        message={errorDialogMessage}
        tone="error"
        onClose={() => setErrorDialogMessage(null)}
      />
    </>
  );
};

export default ManageChamberProfile;
